/**
 * Client errors + web vitals → POST /v1/app/client_errors (P2-owned route, P2 §3.12). Best effort, rate limited,
 * never throws, never sends PII beyond the route. Fixture mode logs to the console instead.
 */
import { API_BASE, APP, getLastRequestId } from './http';

declare const __RELEASE__: string;
let sent = 0;
let fixtures = false;
export const setTelemetryFixtures = (v: boolean) => { fixtures = v; };

export function report(kind: 'error' | 'vitals', message: string, extra: { stack?: string; value?: number } = {}) {
  if (sent++ > 20) return;
  const body = { kind, release: __RELEASE__, route: location.pathname, request_id: getLastRequestId(), message: message.slice(0, 500), stack: extra.stack?.slice(0, 4000), value: extra.value, ua: navigator.userAgent };
  if (fixtures) { console.warn('[telemetry]', body); return; }
  try {
    const blob = new Blob([JSON.stringify(body)], { type: 'text/plain' });
    navigator.sendBeacon?.(`${API_BASE}${APP}/client_errors`, blob);
  } catch { /* ignore */ }
}

export function installGlobalHandlers() {
  addEventListener('error', (e) => report('error', e.message || 'error', { stack: (e.error as Error | undefined)?.stack }));
  addEventListener('unhandledrejection', (e) => {
    const r = e.reason as { message?: string; stack?: string } | undefined;
    report('error', r?.message ?? String(e.reason), { stack: r?.stack });
  });
  try {
    // LCP: the browser reports a new candidate each time a larger element paints; only the final one is the metric, so
    // one beacon per page load (when the page is hidden, or 5 s after the last candidate), never one per candidate.
    let lcp = 0; let timer: ReturnType<typeof setTimeout> | null = null; let done = false;
    const flush = () => { if (done || !lcp) return; done = true; report('vitals', 'LCP', { value: Math.round(lcp) }); };
    new PerformanceObserver((l) => {
      for (const en of l.getEntries()) lcp = Math.max(lcp, en.startTime);
      if (timer) clearTimeout(timer);
      timer = setTimeout(flush, 5000);
    }).observe({ type: 'largest-contentful-paint', buffered: true });
    addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush(); }, { once: true });
  } catch { /* unsupported */ }
}
